import { Icon, Spinner, Text } from '@dixa/lib-shared-habla';
import type { DynamicToolUIPart, ToolUIPart } from 'ai';

import styles from './mimSidekickToolPartIndicator.module.scss';

type MimSidekickToolPartIndicatorProps = {
  part: ToolUIPart | DynamicToolUIPart;
};

const isStringRecord = (value: unknown): value is Record<string, string> =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Object.values(value).every((entry) => typeof entry === 'string');

export const MimSidekickToolPartIndicator = ({
  part,
}: MimSidekickToolPartIndicatorProps): JSX.Element => {
  const stateCopy = isStringRecord(part.toolMetadata?.stateCopy)
    ? part.toolMetadata?.stateCopy[part.state]
    : undefined;

  const { state } = part;

  const renderToolPartIndicator = (): JSX.Element => {
    const isUninitialized = !stateCopy && state !== 'output-error';
    const isLoading =
      state === 'input-streaming' || state === 'input-available';
    const isError = state === 'output-error';

    if (isUninitialized) {
      return (
        <>
          <Spinner size={12} />
          <Text.Paragraph variant="4">
            {'Preparing tool call...'}
          </Text.Paragraph>
        </>
      );
    }

    if (isLoading) {
      return (
        <>
          <Spinner size={12} />
          <Text.Paragraph variant="4">{`${stateCopy}…`}</Text.Paragraph>
        </>
      );
    }

    if (isError) {
      return (
        <>
          <Icon name="close" />
          <Text.Paragraph>
            {stateCopy ?? 'Something went wrong'} -{' '}
            {part.errorText ?? 'unknown error'}
          </Text.Paragraph>
        </>
      );
    }

    return (
      <>
        <Icon name="done" />
        <Text.Paragraph>{stateCopy}</Text.Paragraph>
      </>
    );
  };

  return (
    <div className={styles.toolIndicator}>{renderToolPartIndicator()}</div>
  );
};
